import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Wallet, Wifi, ScrollText } from 'lucide-react';
import type { SupplierFieldMapping } from '@aggregator/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { PageHeader, Loading, ErrorState } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import { FieldMappingEditor } from '@/components/FieldMappingEditor';
import {
  SupplierForm,
  supplierToForm,
  formToPayload,
  type SupplierFormValue,
} from '@/components/SupplierForm';
import {
  useSupplier,
  useUpdateSupplier,
  useSyncSupplier,
  useProbeSupplier,
  useRefreshBalance,
  useSupplierAccount,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import { formatUsdt, formatDateTime, relativeTime } from '@/lib/utils';

type Tab = 'details' | 'mapper';

export function SupplierDetailPage() {
  const { id = '' } = useParams();
  const supplier = useSupplier(id);
  const update = useUpdateSupplier(id);
  const sync = useSyncSupplier(id);
  const probe = useProbeSupplier(id);
  const balance = useRefreshBalance(id);
  const account = useSupplierAccount(id);
  const { toast } = useToast();

  const [tab, setTab] = useState<Tab>('details');
  const [form, setForm] = useState<SupplierFormValue | null>(null);
  const [mappings, setMappings] = useState<SupplierFieldMapping[]>([]);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    if (supplier.data) {
      setForm(supplierToForm(supplier.data));
      setMappings(supplier.data.fieldMappings);
    }
  }, [supplier.data]);

  if (supplier.isLoading || !form) return <Loading />;
  if (supplier.isError) return <ErrorState error={supplier.error} onRetry={() => supplier.refetch()} />;

  const s = supplier.data!;

  function saveDetails() {
    if (!form) return;
    update.mutate(formToPayload(form, 'update'), {
      onSuccess: () => toast({ title: 'Supplier updated', variant: 'success' }),
      onError: (e) => toast({ title: 'Update failed', description: errorMessage(e), variant: 'error' }),
    });
  }

  function saveMappings() {
    update.mutate(
      { fieldMappings: mappings },
      {
        onSuccess: () => toast({ title: 'Mappings saved', variant: 'success' }),
        onError: (e) => toast({ title: 'Save failed', description: errorMessage(e), variant: 'error' }),
      },
    );
  }

  return (
    <div>
      <PageHeader
        title={s.name}
        description={s.slug}
        actions={
          <Button variant="outline" onClick={() => history.back()}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-xs text-muted-foreground">Wallet balance</p>
              <p className="text-lg font-semibold">
                {s.walletBalance == null ? '—' : `${formatUsdt(s.walletBalance)} USDT`}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              loading={balance.isPending}
              onClick={() =>
                balance.mutate(undefined, {
                  onSuccess: (r) => toast({ title: `Balance: ${formatUsdt(r.balance)} USDT`, variant: 'success' }),
                  onError: (e) => toast({ title: 'Failed', description: errorMessage(e), variant: 'error' }),
                })
              }
            >
              <Wallet className="h-4 w-4" /> Refresh
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-xs text-muted-foreground">Account / ledger</p>
              <p className="text-sm font-medium">
                {account.data?.ok
                  ? `${formatUsdt(account.data.balanceUsdt)} ${account.data.currency} · ${account.data.ledger.length} entries`
                  : 'Fetch the supplier profile'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              loading={account.isPending}
              onClick={() => {
                setAccountOpen(true);
                account.mutate(undefined, {
                  onError: (e) => toast({ title: 'Failed', description: errorMessage(e), variant: 'error' }),
                });
              }}
            >
              <ScrollText className="h-4 w-4" /> View
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-xs text-muted-foreground">Last sync</p>
              <p className="text-sm font-medium">{relativeTime(s.lastSyncedAt)}</p>
              {s.lastSyncError ? <p className="text-xs text-destructive">{s.lastSyncError}</p> : null}
            </div>
            <Button
              variant="ghost"
              size="sm"
              loading={sync.isPending}
              onClick={() =>
                sync.mutate(undefined, {
                  onSuccess: (r) =>
                    toast({
                      title: r.ok ? 'Sync complete' : 'Sync failed',
                      description: r.ok ? `${r.fetched} fetched · +${r.created}/${r.updated}` : r.error,
                      variant: r.ok ? 'success' : 'error',
                    }),
                })
              }
            >
              <RefreshCw className="h-4 w-4" /> Sync now
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-xs text-muted-foreground">Connection</p>
              <p className="text-sm font-medium">
                {probe.data ? (
                  <Badge variant={probe.data.ok ? 'success' : 'destructive'}>
                    {probe.data.ok
                      ? `OK · ${probe.data.itemCount ?? 0} items · ${probe.data.latencyMs}ms`
                      : 'Failed'}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">Not tested</span>
                )}
              </p>
              {probe.data && !probe.data.ok ? (
                <p className="text-xs text-destructive">{probe.data.error ?? 'error'}</p>
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="sm"
              loading={probe.isPending}
              onClick={() =>
                probe.mutate(undefined, {
                  onError: (e) => toast({ title: 'Test failed', description: errorMessage(e), variant: 'error' }),
                })
              }
            >
              <Wifi className="h-4 w-4" /> Test
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 flex gap-2 border-b">
        {(['details', 'mapper'] as const).map((t) => (
          <button
            key={t}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
            }`}
            onClick={() => setTab(t)}
          >
            {t === 'details' ? 'Details' : 'Field Mapper'}
          </button>
        ))}
      </div>

      {tab === 'details' ? (
        <Card>
          <CardHeader>
            <CardTitle>API configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SupplierForm value={form} onChange={setForm} mode="update" />
            <div className="flex justify-end">
              <Button loading={update.isPending} onClick={saveDetails}>
                Save changes
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Field mapper</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FieldMappingEditor
              mappings={mappings}
              onChange={setMappings}
              onFetch={() => probe.mutateAsync()}
              hint="Fetch a live sample from this supplier, then auto-match or adjust each field."
            />
            <div className="flex justify-end">
              <Button loading={update.isPending} onClick={saveMappings}>
                Save mappings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        <Link className="hover:underline" to="/products">
          View products from this supplier →
        </Link>
      </p>

      <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} title="Supplier account" wide>
        {account.isPending ? (
          <Loading />
        ) : !account.data ? (
          <p className="text-sm text-muted-foreground">Nothing loaded yet.</p>
        ) : !account.data.ok ? (
          <ErrorState error={account.data.error ?? 'Failed to load account'} />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Balance</p>
                <p className="text-lg font-semibold">
                  {formatUsdt(account.data.balanceUsdt)} {account.data.currency}
                </p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium">Recent ledger ({account.data.ledger.length})</p>
              <Table>
                <THead>
                  <TR>
                    <TH>Date</TH>
                    <TH>Type</TH>
                    <TH>Amount</TH>
                    <TH>Balance after</TH>
                    <TH>Note</TH>
                  </TR>
                </THead>
                <TBody>
                  {account.data.ledger.length > 0 ? (
                    account.data.ledger.map((entry, i) => (
                      <TR key={i}>
                        <TD className="whitespace-nowrap text-muted-foreground">
                          {entry.createdAt ? formatDateTime(entry.createdAt) : '—'}
                        </TD>
                        <TD>{entry.type}</TD>
                        <TD>{formatUsdt(entry.amountUsdt)}</TD>
                        <TD>{entry.balanceAfterUsdt == null ? '—' : formatUsdt(entry.balanceAfterUsdt)}</TD>
                        <TD className="text-muted-foreground">{entry.note}</TD>
                      </TR>
                    ))
                  ) : (
                    <EmptyRow colSpan={5}>This supplier's API returned no ledger entries.</EmptyRow>
                  )}
                </TBody>
              </Table>
            </div>

            <details className="rounded-md border">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Raw response</summary>
              <pre className="max-h-72 overflow-auto border-t bg-muted/30 p-3 text-xs scrollbar-thin">
                {JSON.stringify(account.data.raw, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </Dialog>
    </div>
  );
}
